import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import Modal from "../../../components/base/Modal";
import { Button, Input, Select, notifyError, notifySuccess } from "../../../components/ui";
import { radarCorreoApi, apiErrorMessage, type Provider, type TermsInfo } from "../services/radarCorreoService";
import { PROVIDER_ICON } from "../lib/format";

interface Props {
  status: TermsInfo;
  onClose: () => void;
  onConnected: () => void;
}

type Step = "provider" | "consent" | "connect";

/**
 * Asistente de conexión: proveedor → autorización (texto versionado + periodo
 * de historial) → conectar (ventana de OAuth o formulario IMAP). La ventana de
 * OAuth avisa por postMessage({ source: "mail-intel-oauth" }) y se cierra sola.
 */
export function ConnectWizard({ status, onClose, onConnected }: Props) {
  const { t, i18n } = useTranslation("radarCorreo");
  const lang = i18n.language?.startsWith("en") ? "en" : "es";
  const [step, setStep] = useState<Step>("provider");
  const [provider, setProvider] = useState<Provider | null>(null);
  const [months, setMonths] = useState<number>(status.defaultBackfillMonths);
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [imap, setImap] = useState({ host: "", port: "993", user: "", password: "" });
  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      const d = ev.data as { source?: string; ok?: boolean } | null;
      if (!d || d.source !== "mail-intel-oauth") return;
      setWaiting(false);
      if (d.ok) {
        void notifySuccess(t("wizard.connected"));
        onConnected();
      } else {
        void notifyError(t("wizard.failed"));
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onConnected, t]);

  const consent = { acceptedTerms: accepted, termsVersion: status.version, backfillMonths: months };
  const terms = status.terms[lang];

  const startOAuth = async () => {
    if (!provider || provider === "imap") return;
    // La ventana se abre en el clic (si no, el navegador la bloquea) y luego se le asigna la URL.
    const popup = window.open("", "mail-intel-oauth", "width=520,height=680");
    if (!popup) {
      void notifyError(t("wizard.popupBlocked"));
      return;
    }
    popupRef.current = popup;
    setBusy(true);
    try {
      const { url } = await radarCorreoApi.startOAuth(provider, consent);
      popup.location.href = url;
      setWaiting(true);
    } catch (err) {
      popup.close();
      void notifyError(t("wizard.failed"), apiErrorMessage(err) || undefined);
    } finally {
      setBusy(false);
    }
  };

  const connectImap = async () => {
    setBusy(true);
    try {
      await radarCorreoApi.connectImap({ host: imap.host.trim(), port: Number(imap.port) || 993, user: imap.user.trim(), password: imap.password, ...consent });
      void notifySuccess(t("wizard.connected"));
      onConnected();
    } catch (err) {
      void notifyError(t("wizard.failed"), apiErrorMessage(err) || undefined);
    } finally {
      setBusy(false);
    }
  };

  const providers: Provider[] = ["gmail", "outlook", "imap"];
  const windowOptions = status.backfillWindows.map((m) => ({ value: m, label: m === 0 ? t("wizard.all") : t("wizard.months", { count: m }) }));

  const content = (
    <div className="space-y-4 p-4 sm:p-6" data-testid="radar-connect-wizard">
      <ol className="flex gap-2 text-xs text-content-secondary">
        {(["provider", "consent", "connect"] as Step[]).map((s, i) => (
          <li key={s} className={`rounded-full px-2 py-0.5 ${s === step ? "bg-primary-500/15 font-semibold text-primary-500" : "bg-subtle"}`}>
            {i + 1}. {t(`wizard.steps.${s}`)}
          </li>
        ))}
      </ol>

      {!status.providers.ai && <p className="rounded-lg bg-warning/10 p-3 text-xs text-warning">{t("wizard.aiMissing")}</p>}

      {step === "provider" && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-content">{t("wizard.chooseProvider")}</p>
          {providers.map((p) => {
            const available = status.providers[p];
            return (
              <button
                key={p}
                type="button"
                disabled={!available}
                onClick={() => {
                  setProvider(p);
                  setStep("consent");
                }}
                className="flex w-full items-center gap-3 rounded-lg border border-line p-3 text-left hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-50"
                data-testid={`radar-provider-${p}`}
              >
                <i className={`${PROVIDER_ICON[p]} text-2xl text-primary-500`} />
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-content">{t(`connection.provider.${p}`)}</span>
                  <span className="block text-xs text-content-secondary">{available ? t(`wizard.providerHint.${p}`) : t("wizard.notConfigured")}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {step === "consent" && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-content">{terms.title}</h3>
          <ul className="max-h-56 list-disc space-y-1.5 overflow-y-auto rounded-lg bg-subtle p-3 pl-6 text-xs text-content-secondary" data-testid="radar-terms">
            {terms.points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
          <Select label={t("wizard.backfill")} value={months} onChange={(e) => setMonths(Number(e.target.value))} options={windowOptions} />
          {months === 0 && <p className="text-xs text-warning">{t("wizard.allWarning")}</p>}
          <label className="flex items-center gap-2 text-sm text-content">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid="radar-accept" />
            {t("wizard.accept")}
          </label>
          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep("provider")}>{t("wizard.back")}</Button>
            <Button disabled={!accepted} onClick={() => setStep("connect")} data-testid="radar-consent-next">{t("wizard.next")}</Button>
          </div>
        </div>
      )}

      {step === "connect" && provider && provider !== "imap" && (
        <div className="space-y-3 text-center">
          {waiting ? (
            <p className="text-sm text-content-secondary">{t("wizard.waiting")}</p>
          ) : (
            <Button icon={<i className={PROVIDER_ICON[provider]} />} loading={busy} onClick={startOAuth} data-testid="radar-oauth-start">
              {t("wizard.connectWith", { provider: t(`connection.provider.${provider}`) })}
            </Button>
          )}
          <div>
            <Button variant="ghost" size="sm" onClick={() => setStep("consent")}>{t("wizard.back")}</Button>
          </div>
        </div>
      )}

      {step === "connect" && provider === "imap" && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void connectImap();
          }}
        >
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Input label={t("wizard.imap.host")} value={imap.host} onChange={(e) => setImap({ ...imap, host: e.target.value })} placeholder="imap.miempresa.com" required />
            </div>
            <Input label={t("wizard.imap.port")} value={imap.port} onChange={(e) => setImap({ ...imap, port: e.target.value })} inputMode="numeric" />
          </div>
          <Input label={t("wizard.imap.user")} value={imap.user} onChange={(e) => setImap({ ...imap, user: e.target.value })} autoComplete="username" required />
          <Input label={t("wizard.imap.password")} type="password" value={imap.password} onChange={(e) => setImap({ ...imap, password: e.target.value })} autoComplete="current-password" required />
          <p className="text-xs text-content-secondary">{t("wizard.imap.tls")}</p>
          <div className="flex justify-between">
            <Button variant="ghost" type="button" onClick={() => setStep("consent")}>{t("wizard.back")}</Button>
            <Button type="submit" loading={busy} data-testid="radar-imap-submit">{t("connection.connect")}</Button>
          </div>
        </form>
      )}
    </div>
  );

  return <Modal title={t("wizard.title")} onClose={onClose} content={content} width="max-w-lg w-full" disableCRUD />;
}
